const INACTIVE_FLAG = { ACTIVE: 0, INACTIVE: -1 };
const LOCATIONS_PAGE_SIZE = 50;

const isActive = record => Number(record?.inactive || 0) === INACTIVE_FLAG.ACTIVE;

const byLabel = (first, second) =>
  first.label.localeCompare(second.label, undefined, {
    numeric: true,
    sensitivity: "base",
  });

// Sorting by description makes a row jump away as soon as it is renamed.
const byId = (first, second) => Number(first.id) - Number(second.id);

const sortById = (records = []) => [...records].sort(byId);

const ID_HEADER = { label: "ID", name: "id", type: "label", width: "80px" };

const hasSiteId = siteId => siteId !== null && siteId !== undefined;

// Site 0 ("Booked out") is a real site, so only blank values mean "no site".
const parseSiteId = value => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const siteId = Number(value);
  return Number.isInteger(siteId) && siteId >= 0 ? siteId : null;
};

const getSiteOptions = (warehouseSites = []) =>
  warehouseSites
    .map(site => ({ label: site.name, value: site.id }))
    .sort(byLabel);

const withMissingOption = (options, missingOption) =>
  missingOption && !options.some(option => option.value === missingOption.value)
    ? [...options, missingOption]
    : options;

const getSiteViewingRoomOptions = ({
  viewingRooms = [],
  siteId,
  currentViewingRoom,
} = {}) => {
  const siteOptions = viewingRooms
    .filter(
      viewingRoom =>
        hasSiteId(viewingRoom.siteId) &&
        Number(viewingRoom.siteId) === Number(siteId) &&
        (isActive(viewingRoom) || viewingRoom.id === currentViewingRoom?.id),
    )
    .map(viewingRoom => ({
      label: isActive(viewingRoom)
        ? viewingRoom.description
        : `${viewingRoom.description} (inactive)`,
      value: viewingRoom.id,
    }))
    .sort(byLabel);

  return withMissingOption(
    siteOptions,
    currentViewingRoom && {
      label: `${currentViewingRoom.description} (${
        hasSiteId(currentViewingRoom.siteId) ? "other site" : "no site"
      })`,
      value: currentViewingRoom.id,
    },
  );
};

const countLocationsByViewingRoom = (locations = []) =>
  locations
    .filter(location => !location.deleted && location.viewingRoomId != null)
    .reduce(
      (counts, location) => ({
        ...counts,
        [location.viewingRoomId]: (counts[location.viewingRoomId] || 0) + 1,
      }),
      {},
    );

const getViewingRoomAssignmentCandidates = ({
  viewingRooms = [],
  warehouseSites = [],
  locations = [],
  siteId,
} = {}) => {
  const locationCounts = countLocationsByViewingRoom(locations);
  const siteNameById = new Map(
    warehouseSites.map(site => [Number(site.id), site.name]),
  );

  return viewingRooms
    .filter(
      viewingRoom =>
        !hasSiteId(viewingRoom.siteId) ||
        Number(viewingRoom.siteId) !== Number(siteId),
    )
    .map(viewingRoom => ({
      id: viewingRoom.id,
      description: viewingRoom.description,
      inactive: viewingRoom.inactive,
      currentSiteName: hasSiteId(viewingRoom.siteId)
        ? siteNameById.get(Number(viewingRoom.siteId)) || "Unknown site"
        : null,
      locationsOnSite: locationCounts[viewingRoom.id] || 0,
    }))
    .sort(
      (first, second) =>
        second.locationsOnSite - first.locationsOnSite ||
        (first.description || "").localeCompare(
          second.description || "",
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    );
};

const getSuggestedViewingRoomIds = candidates =>
  candidates
    .filter(candidate => !candidate.currentSiteName && candidate.locationsOnSite > 0)
    .map(candidate => candidate.id);

const toggleSelectedId = (selectedIds, id) =>
  selectedIds.includes(id)
    ? selectedIds.filter(selectedId => selectedId !== id)
    : [...selectedIds, id];

const toggleAllSelectedIds = (selectedIds, ids) =>
  ids.length > 0 && ids.every(id => selectedIds.includes(id)) ? [] : ids;

const getLocationTypeOptions = ({
  locationTypes = [],
  currentType,
} = {}) => {
  const activeOptions = locationTypes
    .filter(
      locationType =>
        isActive(locationType) || locationType.id === currentType?.id,
    )
    .map(locationType => ({
      label: isActive(locationType)
        ? locationType.description
        : `${locationType.description} (inactive)`,
      value: locationType.id,
    }))
    .sort(byLabel);

  return withMissingOption(
    activeOptions,
    currentType && {
      label: `${currentType.description} (inactive)`,
      value: currentType.id,
    },
  );
};

const filterSiteLocations = ({ locations = [], search = "" } = {}) => {
  const normalizedSearch = search.trim().toLowerCase();

  return sortById(
    locations
      .filter(location => !location.deleted)
      .filter(
        location =>
          !normalizedSearch ||
          String(location.id) === normalizedSearch ||
          [location.description, location.type?.description, location.viewingRoom?.description]
            .filter(Boolean)
            .some(value => value.toLowerCase().includes(normalizedSearch)),
      ),
  );
};

const toLocationRow = location => ({
  id: location.id,
  capacity: location.capacity,
  description: location.description,
  type: location.type,
  typeId: location.typeId,
  viewingRoom: location.viewingRoom,
  viewingRoomId: location.viewingRoomId,
});

const LOCATION_UPDATE_FIELDS = [
  "id",
  "capacity",
  "description",
  "typeId",
  "viewingRoomId",
];

const toNullableNumber = value =>
  value === "" || value === null || value === undefined ? null : Number(value);

const toLocationUpdateInput = input =>
  Object.fromEntries(
    Object.entries(input || {})
      .filter(([key]) => LOCATION_UPDATE_FIELDS.includes(key))
      .map(([key, value]) => [
        key,
        key === "capacity" ? toNullableNumber(value) : value,
      ]),
  );

const pickInputFields = (input, fields) =>
  Object.fromEntries(
    Object.entries(input || {}).filter(([key]) => fields.includes(key)),
  );

const pluralizeLocations = count =>
  count === 1 ? "1 linked location" : `${count} linked locations`;

const getViewingRoomDeleteDescription = viewingRoom => {
  const locationCount = Number(viewingRoom?.locationCount || 0);
  const consequence =
    locationCount > 0
      ? `${pluralizeLocations(locationCount)} will have the viewing room cleared.`
      : "No locations use it.";

  return `Delete viewing room "${viewingRoom?.description}"? ${consequence} To keep it for history, make it inactive instead.`;
};

const getLocationTypeDeleteDescription = locationType =>
  `Delete location type "${locationType?.description}"? Any locations using it will have their type cleared. To keep it for history, make it inactive instead.`;

const getMutationError = (result, mutationName) => {
  const payload = result?.data?.[mutationName];
  if (!payload || payload.success) {
    return null;
  }

  return typeof payload.error === "string"
    ? payload.error
    : "Something went wrong saving this change";
};

export {
  ID_HEADER,
  INACTIVE_FLAG,
  LOCATIONS_PAGE_SIZE,
  countLocationsByViewingRoom,
  filterSiteLocations,
  getSuggestedViewingRoomIds,
  getViewingRoomAssignmentCandidates,
  toggleAllSelectedIds,
  toggleSelectedId,
  getLocationTypeDeleteDescription,
  getLocationTypeOptions,
  getMutationError,
  getViewingRoomDeleteDescription,
  getSiteOptions,
  getSiteViewingRoomOptions,
  hasSiteId,
  isActive,
  parseSiteId,
  pickInputFields,
  sortById,
  toLocationRow,
  toLocationUpdateInput,
};
